import { useState, useEffect } from 'react';

// Email validation
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!email) return { isValid: false, message: 'Email is required' };
  if (!emailRegex.test(email)) return { isValid: false, message: 'Please enter a valid email address' };
  return { isValid: true, message: '' };
};

// Password validation
export const validatePassword = (password) => {
  if (!password) return { isValid: false, message: 'Password is required' };
  if (password.length < 8) return { isValid: false, message: 'Password must be at least 8 characters long' };
  
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  
  const strength = [hasUpperCase, hasLowerCase, hasNumbers, hasSpecialChar].filter(Boolean).length;
  
  let message = '';
  if (strength < 2) message = 'Password is too weak';
  else if (strength < 3) message = 'Password is weak';
  else if (strength < 4) message = 'Password is moderate';
  else message = 'Password is strong';
  
  return {
    isValid: password.length >= 8,
    message,
    strength,
    hasUpperCase,
    hasLowerCase,
    hasNumbers,
    hasSpecialChar
  };
};

// Password confirmation validation
export const validatePasswordConfirmation = (password, confirmPassword) => {
  if (!confirmPassword) return { isValid: false, message: 'Please confirm your password' };
  if (password !== confirmPassword) return { isValid: false, message: 'Passwords do not match' };
  return { isValid: true, message: '' };
};

// Form validation
export const validateForm = (formData) => {
  const errors = {};
  
  // Email validation
  const emailValidation = validateEmail(formData.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.message;
  }
  
  // Password validation
  if (formData.password) {
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      errors.password = passwordValidation.message;
    }
  }
  
  // Password confirmation validation
  if (formData.confirmPassword) {
    const confirmValidation = validatePasswordConfirmation(formData.password, formData.confirmPassword);
    if (!confirmValidation.isValid) {
      errors.confirmPassword = confirmValidation.message;
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Real-time validation hook
export const useFieldValidation = (initialValue = '', validator) => {
  const [value, setValue] = useState(initialValue);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState('');
  
  const validation = validator ? validator(value) : { isValid: true, message: '' };
  
  useEffect(() => {
    if (touched) {
      setError(validation.message);
    }
  }, [value, touched, validation.message]);
  
  const handleChange = (newValue) => {
    setValue(newValue);
    if (!touched) setTouched(true);
  };
  
  const handleBlur = () => {
    setTouched(true);
  };
  
  const reset = () => {
    setValue(initialValue);
    setTouched(false);
    setError('');
  };
  
  return {
    value,
    setValue: handleChange,
    touched,
    error: touched ? error : '',
    isValid: validation.isValid,
    validation,
    handleBlur,
    reset
  };
}; 